import { QueryClient } from '@tanstack/react-query'

/**
 * Query keys that survive a logout because they describe the *device*, not the
 * visitor. Everything else is user-scoped and must be torn down with the
 * session.
 */
const DEVICE_SCOPED_KEYS: readonly string[] = ['geolocation']

/**
 * Logout equivalent of `queryClient.resetQueries()`, minus the keys that never
 * belonged to the user.
 *
 * A blanket reset also cleared `['geolocation']`, and `GeoLocationGuard` gates
 * on `isLoading` (`pending && fetching`), so on a geo-gated scene the guard
 * fell back to its spinner and unmounted every actor below it — including the
 * AuthActor that triggered the logout, discarding its state mid-flow. The
 * `/entel` scene is exactly that combination (`set.geolocation.allow: ['CL']`
 * plus `options.forceLogout: true`), so every logged-in arrival saw
 * spinner -> form -> spinner -> remount.
 *
 * Excluding the key is preferable to retaining the value with `placeholderData`:
 * the country does not change because a visitor signed out, so the refetch was
 * never wanted in the first place.
 */
export const resetUserScopedQueries = (queryClient: QueryClient): Promise<void> =>
  queryClient.resetQueries({
    predicate: (query) => !DEVICE_SCOPED_KEYS.includes(String(query.queryKey[0]))
  })
